import { Pipe, PipeTransform } from "@angular/core";

@Pipe({
  name: 'highlight'
})
export class HighlightSearch implements PipeTransform {
  constructor() { }

  transform(value: any, args: any): any {
    if (!args || !value) return value;

    const searchQuery = this.normalizeInput(args);
    if (!searchQuery) return value;

    // Extract HTML tags and replace with placeholders
    const tagMap = new Map<string, string>();
    let textWithPlaceholders = this.extractHtmlTags(value, tagMap);

    // Perform highlighting on clean text
    let highlighted = this.highlightText(textWithPlaceholders, searchQuery);

    // Restore HTML tags
    let result = this.restoreHtmlTags(highlighted, tagMap);

    return result;
  }

  /**
   * Normalize the search input by decoding URI components and removing quotes
   */
  private normalizeInput(input: any): string {
    let normalized: string;
    
    if (typeof input === 'string') {
      try {
        normalized = decodeURIComponent(input);
      } catch {
        normalized = input;
      }
    } else {
      normalized = String(input);
    }

    return normalized.replaceAll(/['"]/g, '').trim();
  }

  /**
   * Extract all HTML tags and replace them with unique placeholders
   */
  private extractHtmlTags(html: string, tagMap: Map<string, string>): string {
    let counter = 0;
    return html.replace(/<[^>]+>/g, (tag) => {
      const placeholder = `__TAG_${counter}__`;
      tagMap.set(placeholder, tag);
      counter++;
      return placeholder;
    });
  }

  /**
   * Restore HTML tags from placeholders
   */
  private restoreHtmlTags(text: string, tagMap: Map<string, string>): string {
    let result = text;
    tagMap.forEach((tag, placeholder) => {
      result = result.replace(new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), tag);
    });
    return result;
  }

  /**
   * Main highlighting logic with Arabic support
   */
  private highlightText(text: string, searchQuery: string): string {
    const arabicVariations = [
      { a: "ا", b: "أ" }, { a: "ا", b: "إ" }, { a: "ا", b: "ى" },
      { a: "أ", b: "ى" }, { a: "أ", b: "إ" }, { a: "إ", b: "ى" },
      { a: "ه", b: "ة" }, { a: "و", b: "ؤ" }, { a: "ت", b: "ة" },
      { a: "ض", b: "ظ" }, { a: "ئ", b: "ء" },
    ];

    // Split search query into terms
    const searchTerms = searchQuery.split(/\s+/).filter(term => term.length > 0);
    let result = text;

    // Process each search term
    searchTerms.forEach((term) => {
      // Generate all variations of the search term
      const variations = this.generateArabicVariations(term, arabicVariations);

      // Highlight each variation
      variations.forEach((variation) => {
        result = this.highlightTerm(result, variation);
      });
    });

    return result;
  }

  /**
   * Generate Arabic character variations for a given term
   */
  private generateArabicVariations(term: string, variations: Array<{ a: string; b: string }>): Set<string> {
    const termVariations = new Set<string>();
    
    // Add the original term
    termVariations.add(term);

    // Generate variations by replacing Arabic characters
    variations.forEach((rel) => {
      const variant = term.replace(new RegExp(rel.a, 'g'), rel.b);
      if (variant !== term) {
        termVariations.add(variant);
      }
    });

    // Handle Arabic prefixes (الـ)
    if (term.startsWith("ال")) {
      termVariations.add(term.substring(2)); // Remove prefix
    } else {
      termVariations.add(`ال${term}`); // Add prefix
    }

    return termVariations;
  }

  /**
   * Highlight a specific term in text using word boundaries
   */
  private highlightTerm(text: string, term: string): string {
    if (!term) return text;

    // Escape special regex characters
    const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    
    // Use word boundaries for better matching - avoid matching inside already highlighted text
    const pattern = new RegExp(`\\b${escapedTerm}\\b(?![^<]*>__TAG)`, 'gi');
    
    return text.replace(pattern, (match) => {
      // Avoid double highlighting
      if (match.includes('<mark')) {
        return match;
      }
      return `<mark class='highlightedtext'>${match}</mark>`;
    });
  }

  /**
   * Utility to validate regex patterns
   */
  static makeValidRegExp(input: string): string {
    try {
      new RegExp(input);
      return input;
    } catch (error) {
      if (error instanceof SyntaxError) {
        return input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      }
      throw error;
    }
  }
}
